import { scheduleIn } from "app/actions/schedule";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

interface Props {
  onClose: () => void;
  onScheduleChange: (pickedAt: number, pickedTime: string) => void;
}

export const useScheduleModal = ({ onClose, onScheduleChange }: Props) => {
  const dispatch = useDispatch();

  const [selectedValue, setSelectedValue] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>("Days");
  const [selectOptions, setSelectOptions] = useState<number[]>([]);
  const selectTimeOptions = useMemo(() => ["Hours", "Days", "Weeks"], []);

  useMemo(() => {
    dispatch(
      scheduleIn({
        pickedAt: selectedValue === null ? null : selectedValue,
        pickedIn: selectedTime,
      }),
    );
  }, [dispatch, selectedTime, selectedValue]);

  const optionValues = useCallback((maxNum: number) => {
    const options = [] as any;
    for (let i = 1; i <= maxNum; i++) {
      options.push(i);
    }
    setSelectOptions(options);
  }, []);

  useMemo(() => {
    optionValues(31);
  }, [optionValues]);

  const handleScheduleSelect = useCallback(
    (pickedAt: number | null) => {
      setSelectedValue(pickedAt);
      if (pickedAt !== null && pickedAt > 0) {
        setSelectOptions((prev) => {
          if (!prev.includes(0)) {
            return [0, ...prev];
          }
          return prev;
        });
      } else {
        setSelectOptions((prev) => prev.filter((a) => a !== 0));
      }
    },
    [selectOptions],
  );

  const handleScheduleTimeSelect = useCallback(
    (pickedTime: string) => {
      let maxOptionValue: number;
      setSelectedTime(pickedTime);

      if (pickedTime === "Hours") {
        maxOptionValue = 24;
      } else if (pickedTime === "Weeks") {
        maxOptionValue = 52;
      } else {
        maxOptionValue = 31;
      }

      setSelectedValue((prevValue: number) => {
        const selectedValueInt = prevValue;
        if (prevValue !== null && selectedValueInt > maxOptionValue) {
          return null;
        } else {
          return prevValue;
        }
      });

      optionValues(maxOptionValue);
    },
    [optionValues],
  );

  const handleSubmit = useCallback(() => {
    let pickedAt: number | null;
    if (selectedValue === 0 || !selectedValue) {
      pickedAt = null;
    } else {
      pickedAt = selectedValue;
    }

    onScheduleChange(pickedAt, selectedTime);
    onClose();
  }, [onClose, onScheduleChange, selectedValue, selectedTime]);

  return {
    selectOptions,
    handleScheduleSelect,
    handleScheduleTimeSelect,
    handleSubmit,
    selectedValue,
    selectTimeOptions,
    selectedTime,
  };
};
